import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  Skeleton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  type TextFieldProps,
} from '@mui/material';
import { useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';
import { ApiError } from '../../../api/client';
import { fonts } from '../../../design/theme';
import { Money } from '../../../components/Money';
import { StatusChip } from '../../../components/StatusChip';
import { errorMessage } from '../../../lib/notify';
import { useShop } from '../../../shop/ShopProvider';

// --- status chips ----------------------------------------------------------------------------

const PO_LABELS: Record<string, string> = {
  DRAFT: 'Draft',
  CONFIRMED: 'Confirmed',
  PARTIALLY_RECEIVED: 'Partly received',
  RECEIVED: 'Received',
  CLOSED: 'Closed',
  CANCELLED: 'Cancelled',
};

export function OrderStatusChip({ status }: { status: string }) {
  return (
    <StatusChip status={status === 'CLOSED' ? 'COMPLETED' : status} label={PO_LABELS[status]} />
  );
}

export function ReceiptStatusChip({ status }: { status: string }) {
  return (
    <StatusChip
      status={status === 'REVERSED' ? 'VOIDED' : 'RECEIVED'}
      label={status === 'REVERSED' ? 'Reversed' : 'Posted'}
    />
  );
}

/** A bill's lifecycle and payment state in one chip: Cancelled, Overdue, Paid, Part paid, Unpaid. */
export function BillStatusChip({
  status,
  paymentStatus,
  overdue,
}: {
  status: string;
  paymentStatus: string;
  overdue?: boolean;
}) {
  if (status === 'CANCELLED') return <StatusChip status="CANCELLED" />;
  if (status === 'DRAFT') return <StatusChip status="DRAFT" />;
  if (overdue && paymentStatus !== 'PAID') return <StatusChip status="OVERDUE" />;
  return (
    <StatusChip
      status={paymentStatus}
      label={paymentStatus === 'PARTIALLY_PAID' ? 'Part paid' : undefined}
    />
  );
}

// --- page scaffolding ------------------------------------------------------------------------

/** Detail page header: back link, eyebrow, title + chips, subtitle and actions. */
export function DocHeader({
  back,
  eyebrow,
  title,
  chips,
  subtitle,
  actions,
}: {
  back: { to: string; label: string };
  eyebrow?: string;
  title: ReactNode;
  chips?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <Box sx={{ mb: 3 }}>
      <Button
        component={RouterLink}
        to={back.to}
        size="small"
        startIcon={<ArrowBackRounded />}
        sx={{ mb: 1.5, ml: -1, color: 'text.secondary', fontWeight: 600 }}
      >
        {back.label}
      </Button>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{ justifyContent: 'space-between', alignItems: { md: 'flex-end' } }}
      >
        <Box sx={{ minWidth: 0 }}>
          {eyebrow && (
            <Typography
              variant="overline"
              color="primary"
              sx={{ fontWeight: 700, letterSpacing: '0.08em', lineHeight: 1.6 }}
            >
              {eyebrow}
            </Typography>
          )}
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="h3" sx={{ wordBreak: 'break-word' }}>
              {title}
            </Typography>
            {chips}
          </Stack>
          {subtitle && (
            <Typography color="text.secondary" component="div" sx={{ mt: 0.75 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {actions && (
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            sx={{ flexWrap: 'wrap', justifyContent: { md: 'flex-end' } }}
          >
            {actions}
          </Stack>
        )}
      </Stack>
    </Box>
  );
}

/** Responsive grid of summary cards. */
export function SummaryGrid({ children, min = 220 }: { children: ReactNode; min?: number }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: {
          xs: 'repeat(2, minmax(0, 1fr))',
          lg: `repeat(auto-fit, minmax(${String(min)}px, 1fr))`,
        },
        mb: 3,
      }}
    >
      {children}
    </Box>
  );
}

export function SummaryCard({
  label,
  icon,
  children,
  hint,
  accent,
}: {
  label: string;
  icon?: ReactNode;
  children: ReactNode;
  hint?: ReactNode;
  accent?: 'primary' | 'success' | 'warning' | 'error' | 'info';
}) {
  return (
    <Card sx={{ p: { xs: 2, sm: 2.5 }, position: 'relative', overflow: 'hidden', minWidth: 0 }}>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mb: 1.25 }}>
        {icon && (
          <Box
            sx={(t) => {
              const c = (t.vars ?? t).palette[accent ?? 'primary'].main;
              return {
                width: 34,
                height: 34,
                borderRadius: '10px',
                display: 'grid',
                placeItems: 'center',
                color: c,
                bgcolor: `color-mix(in srgb, ${c} 12%, transparent)`,
                '& svg': { fontSize: 19 },
              };
            }}
          >
            {icon}
          </Box>
        )}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}
        >
          {label}
        </Typography>
      </Stack>
      <Box
        sx={{
          fontSize: { xs: '1.0625rem', sm: '1.375rem' },
          fontWeight: 700,
          wordBreak: 'break-word',
          fontFamily: fonts.display,
          letterSpacing: '-0.01em',
        }}
      >
        {children}
      </Box>
      {hint && (
        <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: 0.75 }}>
          {hint}
        </Typography>
      )}
    </Card>
  );
}

/** Labelled progress bar with a percentage. */
export function ProgressMeter({
  label,
  value,
  color = 'primary',
  caption,
}: {
  label: string;
  value: number;
  color?: 'primary' | 'success' | 'warning' | 'info';
  caption?: ReactNode;
}) {
  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', mb: 0.75 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography
          variant="body2"
          className="tabular"
          sx={{ fontWeight: 650, whiteSpace: 'nowrap', pl: 1 }}
        >
          {Math.round(value)}%
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={Math.min(100, Math.max(0, value))}
        color={color}
        sx={{ height: 8, bgcolor: 'action.hover' }}
        aria-label={label}
      />
      {caption && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.75 }}>
          {caption}
        </Typography>
      )}
    </Box>
  );
}

/** A card section with a title row. */
export function Section({
  title,
  action,
  children,
  dense,
}: {
  title: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  dense?: boolean;
}) {
  return (
    <Card sx={{ mb: 3 }}>
      <Stack
        direction="row"
        sx={{
          px: 2.5,
          py: 1.75,
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 700 }}>
          {title}
        </Typography>
        {action}
      </Stack>
      <Box sx={{ p: dense ? 0 : 2.5 }}>{children}</Box>
    </Card>
  );
}

/** Two-column facts list (label / value). */
export function Facts({ items }: { items: [string, ReactNode][] }) {
  return (
    <Box
      component="dl"
      sx={{
        m: 0,
        display: 'grid',
        gridTemplateColumns: 'minmax(110px, auto) 1fr',
        columnGap: 2,
        rowGap: 1.25,
      }}
    >
      {items.map(([k, v]) => (
        <Box key={k} sx={{ display: 'contents' }}>
          <Typography component="dt" variant="body2" color="text.secondary">
            {k}
          </Typography>
          <Typography component="dd" variant="body2" sx={{ m: 0, fontWeight: 550 }}>
            {v}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

// --- states ----------------------------------------------------------------------------------

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const requestId = error instanceof ApiError ? error.requestId : undefined;
  const notFound = error instanceof ApiError && error.status === 404;
  const forbidden = error instanceof ApiError && error.status === 403;
  return (
    <Card sx={{ p: { xs: 4, sm: 6 }, textAlign: 'center' }}>
      <Box
        sx={(t) => ({
          width: 56,
          height: 56,
          mx: 'auto',
          mb: 2,
          borderRadius: '16px',
          display: 'grid',
          placeItems: 'center',
          color: 'error.main',
          bgcolor: `color-mix(in srgb, ${(t.vars ?? t).palette.error.main} 10%, transparent)`,
        })}
      >
        <ErrorOutlineRounded />
      </Box>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        {notFound
          ? 'Not found'
          : forbidden
            ? 'You don’t have access to this'
            : 'Couldn’t load this'}
      </Typography>
      <Typography color="text.secondary" sx={{ maxWidth: 440, mx: 'auto' }}>
        {notFound ? 'It may have been removed, or the link is wrong.' : errorMessage(error)}
      </Typography>
      {requestId && (
        <Typography variant="caption" color="text.disabled" component="div" sx={{ mt: 1 }}>
          Request ID {requestId}
        </Typography>
      )}
      {onRetry && !notFound && !forbidden && (
        <Button variant="outlined" sx={{ mt: 2.5 }} onClick={onRetry}>
          Try again
        </Button>
      )}
    </Card>
  );
}

/** Skeleton shaped like a document detail page. */
export function DetailSkeleton() {
  return (
    <Box>
      <Skeleton width={120} height={28} />
      <Skeleton width={260} height={48} sx={{ mb: 3 }} />
      <SummaryGrid>
        {[0, 1, 2, 3].map((i) => (
          <Card key={i} sx={{ p: 2.5 }}>
            <Skeleton width="50%" />
            <Skeleton width="70%" height={36} />
          </Card>
        ))}
      </SummaryGrid>
      <Card sx={{ p: 2.5 }}>
        {[0, 1, 2, 3, 4].map((i) => (
          <Skeleton key={i} height={40} />
        ))}
      </Card>
    </Box>
  );
}

// --- inputs ----------------------------------------------------------------------------------

/** Numeric text field: decimal keyboard, right-aligned tabular digits. */
export function DecimalField({
  inputProps,
  slotProps,
  ...props
}: TextFieldProps & { inputProps?: object }) {
  return (
    <TextField
      {...props}
      slotProps={{
        ...slotProps,
        htmlInput: {
          inputMode: 'decimal',
          className: 'tabular',
          style: { textAlign: 'right' },
          ...inputProps,
          ...(slotProps?.htmlInput as object),
        },
      }}
    />
  );
}

/** Discount value with a % / currency switch. */
export function DiscountInput({
  type,
  value,
  onType,
  onValue,
  error,
  label = 'Discount',
  size,
}: {
  type: 'AMOUNT' | 'PERCENT';
  value: string;
  onType: (t: 'AMOUNT' | 'PERCENT') => void;
  onValue: (v: string) => void;
  error?: string;
  label?: string;
  size?: 'small' | 'medium';
}) {
  const { format } = useShop();
  return (
    <DecimalField
      label={label}
      value={value}
      size={size}
      onChange={(e) => onValue(e.target.value)}
      error={!!error}
      helperText={error}
      placeholder="0"
      slotProps={{
        inputLabel: { shrink: true },
        input: {
          endAdornment: (
            <InputAdornment position="end" sx={{ mr: -1 }}>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={type}
                onChange={(_, v: 'AMOUNT' | 'PERCENT' | null) => v && onType(v)}
                sx={{ '& .MuiToggleButton-root': { px: 1, py: 0.25, fontSize: '0.75rem' } }}
              >
                <ToggleButton value="PERCENT" aria-label="percent">
                  %
                </ToggleButton>
                <ToggleButton value="AMOUNT" aria-label="amount">
                  {format.currencyCode}
                </ToggleButton>
              </ToggleButtonGroup>
            </InputAdornment>
          ),
        },
      }}
    />
  );
}

// --- totals ----------------------------------------------------------------------------------

export function TotalsRows({
  rows,
  total,
  totalLabel = 'Total',
}: {
  rows: { label: ReactNode; value: string; negative?: boolean; muted?: boolean }[];
  total: string;
  totalLabel?: string;
}) {
  return (
    <Box>
      {rows.map((r, i) => (
        <Stack
          key={i}
          direction="row"
          sx={{
            justifyContent: 'space-between',
            py: 0.75,
            color: r.muted ? 'text.secondary' : undefined,
          }}
        >
          <Typography variant="body2" color="text.secondary" component="div">
            {r.label}
          </Typography>
          <Typography variant="body2" component="div" sx={{ fontWeight: 550 }}>
            {r.negative && Number(r.value) !== 0 ? '− ' : ''}
            <Money value={r.value} />
          </Typography>
        </Stack>
      ))}
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'baseline',
          mt: 1,
          pt: 1.5,
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <Typography sx={{ fontWeight: 700 }}>{totalLabel}</Typography>
        <Typography sx={{ fontWeight: 750, fontSize: '1.375rem', letterSpacing: '-0.01em' }}>
          <Money value={total} />
        </Typography>
      </Stack>
    </Box>
  );
}

// --- dialogs ---------------------------------------------------------------------------------

/** Confirmation that asks for a reason (cancel, reverse, close short). */
export function ReasonDialog({
  open,
  title,
  body,
  confirmLabel,
  reasonRequired = true,
  danger = true,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel: string;
  reasonRequired?: boolean;
  danger?: boolean;
  onConfirm: (reason: string) => Promise<unknown>;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const tooShort = reason.trim().length > 0 && reason.trim().length < 3;
  const missing = reasonRequired && reason.trim().length < 3;
  const close = () => {
    if (busy) return;
    setReason('');
    setError(null);
    onClose();
  };
  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" component="div" sx={{ mb: 2 }}>
          {body}
        </Typography>
        <TextField
          autoFocus
          label={reasonRequired ? 'Reason' : 'Reason (optional)'}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          multiline
          minRows={2}
          error={tooShort}
          helperText={tooShort ? 'At least 3 characters' : ' '}
          slotProps={{ htmlInput: { maxLength: 500 } }}
        />
        {!!error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {errorMessage(error)}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button variant="outlined" onClick={close} disabled={busy}>
          Keep it
        </Button>
        <Button
          variant="contained"
          color={danger ? 'error' : 'primary'}
          loading={busy}
          disabled={missing || tooShort}
          onClick={() => {
            setBusy(true);
            setError(null);
            onConfirm(reason.trim())
              .then(() => {
                setReason('');
                onClose();
              })
              .catch((e: unknown) => setError(e))
              .finally(() => setBusy(false));
          }}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Icon button with a tooltip (shows why it is disabled). */
export function TipIconButton({
  title,
  onClick,
  children,
  disabled,
}: {
  title: string;
  onClick: () => void;
  children: ReactNode;
  disabled?: boolean;
}) {
  return (
    <Tooltip title={title}>
      <span>
        <IconButton aria-label={title} onClick={onClick} disabled={disabled} size="small">
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
}
